import { useCallback } from 'react'

import type { Map } from 'immutable'
import { useSelector } from 'react-redux'

import type { RecipeCoreId, RecipeUUID } from '@library/menu-service'

import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { getBasketPostcode, getBasketRecipes } from 'selectors/basket'

import { sumQuantities, useRecipeLimitReached } from '../limits'
import { useAddRecipeByUuid } from './useAddRecipe'
import { useRemoveRecipeByUuid } from './useRemoveRecipe'

/**
 * Can recipes be added to the basket at this point?
 *
 * Requires a postcode to be set
 *
 * @returns
 */
const useCanAddRecipes = () => {
  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const postcode = useSelector(getBasketPostcode)

  if (hasProspectNotSeenWizard) return true

  return Boolean(postcode)
}

/**
 * Get the recipes currently in the basket
 *
 * @returns a `Map<RecipeCoreId, number>` where the key is the recipe ID and the value is the number of that recipe in basket
 */
const useRecipeQuantities = () =>
  useSelector(getBasketRecipes) as unknown as Map<RecipeCoreId, number>

const useRecipeQuantitiesByUuid = (): Record<RecipeUUID, number> => {
  return useSelector(getBasketRecipes).toJS()
}

/**
 * Entrypoint for the `basket recipes` information
 *
 * @returns addRecipe - add a recipe to the basket
 * @returns removeRecipe - remove a recipe from the basket
 * @returns swapRecipe - replace a recipe in the basket
 *
 */
export const useBasketRecipes = () => {
  const recipeQuantities = useRecipeQuantities()
  const recipeQuantitiesByUuid = useRecipeQuantitiesByUuid()

  const getQuantitiesForRecipeId = useCallback(
    (recipeId: RecipeUUID) => recipeQuantitiesByUuid[recipeId] || 0,
    [recipeQuantitiesByUuid],
  )

  const addRecipeByUuid = useAddRecipeByUuid()
  const removeRecipeByUuid = useRemoveRecipeByUuid()
  const reachedLimit = useRecipeLimitReached(recipeQuantities)
  const canAddRecipes = useCanAddRecipes()

  const recipeCount = sumQuantities(recipeQuantities)

  return {
    addRecipeByUuid,
    removeRecipeByUuid,
    canAddRecipes,
    reachedLimit,
    recipeCount,
    getQuantitiesForRecipeId,
  }
}
